/**
 * Base garments (BLU-1394, Products > Base garments): the pure rules shared by
 * the backend and the browser. No calls, no `t()`: problems and errors are
 * codes, the UI words them.
 *
 *  - `composeBlankSku`: the one place a blank's SKU text is composed.
 *  - `composeSetUp`: why a code is not ready (blocking checks 1-7), plus the
 *    warnings (8-12). Fed the list RPC's compact facts and the org's code
 *    tables, so the default size table applies when the org has no settings row.
 *  - `suggestLabelPrefix`: the prefix a client's labelled codes start with.
 *  - The prices-style CSV: `parseBaseGarmentsCsv`, `buildBaseGarmentsCsv` and
 *    `diffBaseGarmentsCsv` (a browser diff against the export payload).
 */
import { CLIENT_PRICE_REGEX } from '../schemas/client-prices.js';
import { BASE_GARMENT_CODE_RE, BASE_GARMENT_CODE_TABLE_MAX, BASE_GARMENT_IMAGE_URL_RE, BASE_GARMENT_LINKABLE_CODE_RE, BASE_GARMENT_MAX_IMPORT_ROWS, BASE_GARMENT_SEGMENT_NAME_MAX, BASE_GARMENT_SIZE_COLOUR_CODE_RE, BASE_GARMENT_STOCK_MAX, BASE_GARMENT_WEIGHT_MAX_GRAMS, NEW_PRODUCT_CODE_RE, } from '../schemas/base-garments.js';
import { DEFAULT_COLOURLESS_CODE, splitSupplyBlankCode } from './product-sync.js';
// ── SKU text ────────────────────────────────────────────────────────────────
/** `CODE-SIZE-COLOUR`, or `CODE-COLOUR` for a size-less code. Upper-cased. */
export function composeBlankSku(code, size, colour) {
    return [code, size, colour]
        .filter((segment) => typeof segment === 'string' && segment.trim() !== '')
        .map((segment) => segment.trim())
        .join('-')
        .toUpperCase();
}
/**
 * The prefix of a client's labelled codes (`TH` + `TEE`): the initials of the
 * first two words, else the humps of one camel-case word, else its first two
 * letters. Latin letters and digits only; '' when nothing usable is left (a
 * name in Chinese) or the result would not start with a letter.
 */
export function suggestLabelPrefix(clientName) {
    const words = clientName
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .split(/[^A-Za-z0-9]+/)
        .filter((word) => word !== '');
    let letters;
    if (words.length >= 2) {
        letters = `${words[0][0]}${words[1][0]}`;
    }
    else if (words.length === 1) {
        const humps = words[0].match(/[A-Z][a-z0-9]*|[a-z0-9]+/g) ?? [];
        letters = humps.length >= 2 ? `${humps[0][0]}${humps[1][0]}` : words[0].slice(0, 2);
    }
    else {
        return '';
    }
    const prefix = letters.toUpperCase();
    return /^[A-Z]/.test(prefix) ? prefix : '';
}
function unreadable(reason, extra = {}) {
    return {
        key: 'unreadable',
        severity: 'blocking',
        reason,
        ...(reason === 'notLinked' || reason === 'linkRetired' ? { action: 'link' } : {}),
        ...extra,
    };
}
/** An unlinked code's problem: the plain "Not linked", or the reason the dry run found. */
function unlinkedProblem(link) {
    const offending = link?.offendingSku ? { offendingSku: link.offendingSku } : {};
    switch (link?.status) {
        case 'finished_goods':
            return unreadable('finishedGoods');
        case 'needs_review':
            return unreadable('needsReview', offending);
        case 'mixed_shape':
            return unreadable('mixedShape', offending);
        case 'retired':
            return unreadable('linkRetired');
        default:
            return unreadable('notLinked');
    }
}
function readableProblem(facts, codeTables) {
    if (!BASE_GARMENT_LINKABLE_CODE_RE.test(facts.code))
        return unreadable('codeFormat');
    const tables = {
        productTypeCodes: Object.values(codeTables.productTypes),
        sizeCodes: Object.values(codeTables.sizes),
    };
    // "Add these codes" adds a product code of 1 to 10 capital letters or digits (the
    // code-table rule), so every linkable code checked above gets the action.
    const addCodes = BASE_GARMENT_CODE_RE.test(facts.code) ? { action: 'addCodes' } : {};
    const sizedCode = splitSupplyBlankCode(`${facts.code}-NC`, tables);
    if (!sizedCode.ok && sizedCode.reason === 'unknown_product_type')
        return unreadable('productType', addCodes);
    const badSizes = facts.sizeSegs.filter((seg) => {
        const split = splitSupplyBlankCode(`${facts.code}-${seg}-NC`, tables);
        return !split.ok && split.reason === 'unknown_size';
    });
    if (badSizes.length > 0)
        return unreadable('size', { items: badSizes, count: badSizes.length, ...addCodes });
    if (facts.shapes.length > 1)
        return unreadable('mixedShape');
    if (!facts.linked)
        return unreadable('notLinked');
    // Linked, but to the wrong shape: orders key to a SKU that does not exist. 3 parts =
    // code, size and colour; 2 = code and colour. Only the org's own link can be re-linked.
    const shape = facts.shapes[0];
    if (facts.linkedKeep != null && facts.shapes.length === 1 && shape !== undefined && shape !== facts.linkedKeep) {
        return unreadable('linkShape', {
            linkKeep: facts.linkedKeep,
            shapeKeep: shape,
            ...(facts.linkSource === 'org' ? { action: 'relink' } : {}),
        });
    }
    return null;
}
/**
 * A code made for one client (audience `client`) whose list RPC sends `clientUnlinked`.
 * The client's orders reach its blanks through the client's own back-up links (keyed by
 * the blank's id), not through this org's code tables or SKU rules, and it is the
 * back-up model, not blank supply. So such a code is judged by those links: no
 * "can't be read", and a missing cost is worth a look (a warning), not a blocker.
 * "No client takes blanks" becomes "the client isn't connected here" (`clientNotConnected`:
 * no active junction at the location; consent does not matter). An older RPC without
 * the facts keeps the old rules.
 */
const isClientLabelled = (facts) => facts.audience === 'client' && facts.clientUnlinked !== undefined;
/**
 * The Set up cell of one code: blocking checks 1-7 in this fixed order, then
 * warnings 8-12. The first blocking problem is the headline, the rest "+N more".
 * A code made for one client swaps check 3 (readable) for its client links and
 * check 7 (consent) for its client's connection at this location (an active
 * junction; blank supply consent does not matter); its missing cost is a warning
 * (see `isClientLabelled`).
 */
export function composeSetUp(facts, options) {
    const blocking = [];
    const push = (problem) => {
        blocking.push({ ...problem, severity: 'blocking' });
    };
    const clientLabelled = isClientLabelled(facts);
    if (facts.variantCount === 0)
        push({ key: 'noVariants' });
    if (facts.missingInventoryRow > 0)
        push({ key: 'missingInventoryRow', count: facts.missingInventoryRow });
    if (facts.variantCount > 0) {
        if (clientLabelled) {
            if (facts.clientUnlinked > 0)
                push({ key: 'clientUnlinked', count: facts.clientUnlinked });
        }
        else {
            const problem = readableProblem(facts, options.codeTables);
            if (problem)
                blocking.push(problem);
        }
    }
    if (options.weightRequired && !facts.hasDefaultWeight && facts.sizesWithoutWeight.length > 0) {
        const sizes = facts.sizesWithoutWeight.filter((size) => size !== null);
        push({ key: 'missingWeight', items: sizes, count: facts.sizesWithoutWeight.length });
    }
    if (facts.costMissing && !clientLabelled)
        push({ key: 'missingCost' });
    if (facts.shapeWrongCount > 0) {
        push({
            key: 'shapeWrong',
            items: facts.shapeWrong.map((entry) => entry.sku),
            count: facts.shapeWrongCount,
            shapeReasons: [...new Set(facts.shapeWrong.map((entry) => entry.reason))],
            ...(facts.shapeWrong.some((entry) => entry.reason === 'shared_mismatch')
                ? { action: 'reapplyAudience' }
                : {}),
        });
    }
    if (clientLabelled) {
        if (facts.clientNotConnected === true)
            push({ key: 'clientNotConnected' });
    }
    else if (facts.noConsent) {
        push({ key: 'noConsent' });
    }
    const warnings = [];
    // From the facts, so every code carries it (page or not); it never blocks here.
    if (facts.costMissing && clientLabelled)
        warnings.push({ key: 'missingCost', severity: 'warning' });
    const w = options.warnings;
    if (w) {
        const warn = (key) => {
            warnings.push({ key, severity: 'warning' });
        };
        if (w.noPrintSizeRule)
            warn('noPrintSizeRule');
        if (w.missingImage)
            warn('missingImage');
        if (w.noLowStockAlert)
            warn('noLowStockAlert');
        if (w.unpricedClient)
            warn('unpricedClient');
        if (w.sizeLessWithPreprint)
            warn('sizeLessWithPreprint');
    }
    return { ready: blocking.length === 0, blocking, warnings };
}
/**
 * Refine a composed Set up with the dry run's answer for its unlinked code (the
 * browser fetches it lazily, after the list): "Not linked" becomes the real reason
 * (finished products, a mixed shape, a link turned off on purpose, a blank to review),
 * in the same place and still blocking. A set up without a plain "Not linked"
 * problem, or an answer that adds nothing, comes back as is.
 */
export function applyLinkStatus(setUp, link) {
    if (!link)
        return setUp;
    const at = setUp.blocking.findIndex(isNotLinkedProblem);
    if (at < 0)
        return setUp;
    const refined = unlinkedProblem(link);
    if (refined.reason === 'notLinked')
        return setUp;
    const blocking = [...setUp.blocking];
    blocking[at] = refined;
    return { ...setUp, blocking };
}
/** The plain "Not linked" problem, the one a dry run can refine. */
export const isNotLinkedProblem = (problem) => problem.key === 'unreadable' && problem.reason === 'notLinked';
// ── CSV ─────────────────────────────────────────────────────────────────────
/**
 * The export and template columns, in order. Headers stay English like every CSV
 * in the app. `stock_type` was `who_can_use` before the stock type was named; the
 * upload still reads that header (`CSV_HEADER_ALIASES`).
 */
export const BASE_GARMENT_CSV_COLUMNS = [
    'sku',
    'product_code',
    'name',
    'size',
    'colour',
    'stock_type',
    'print_method',
    'weight_g',
    'cost_usd',
    'low_stock_alert',
    'supplier',
    'hs_code',
    'stock',
    'image_url',
];
/** Two cells say the same: trimmed, any case. */
const sameText = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();
/** The stock type words a file is written with (English, like the headers). */
export const STOCK_TYPE_OWN_TEXT = 'Own stock';
export const STOCK_TYPE_SHARED_TEXT = 'Shared';
export const STOCK_TYPE_LABELLED_TEXT = 'Labelled for';
/** `Own stock`, `Shared` or `Labelled for <client name>` (`Labelled for a client` with no name). */
export function baseGarmentStockTypeText(row) {
    if (row.audience === 'own')
        return STOCK_TYPE_OWN_TEXT;
    if (row.audience === 'all_clients')
        return STOCK_TYPE_SHARED_TEXT;
    return `${STOCK_TYPE_LABELLED_TEXT} ${row.clientName || 'a client'}`;
}
/**
 * A stock type cell, read in any case: the words the export writes (`Own stock`,
 * `Shared`, `Labelled for <client>`, also spelled `Labeled`), the stored values
 * (`own`, `all_clients`), and the words a file had before the stock type was
 * named (`Only us`, `All clients`, `Only <client>`), so an old file still
 * uploads. Null: not a stock type.
 */
export function readBaseGarmentStockType(text) {
    const value = text.trim();
    if ([STOCK_TYPE_OWN_TEXT, 'own', 'Only us'].some((word) => sameText(value, word)))
        return { audience: 'own' };
    if ([STOCK_TYPE_SHARED_TEXT, 'all_clients', 'All clients'].some((word) => sameText(value, word))) {
        return { audience: 'all_clients' };
    }
    const client = /^(?:label(?:l)?ed\s+for|only)\s+(\S.*)$/i.exec(value);
    return client ? { audience: 'client', clientName: client[1].trim() } : null;
}
const FORMULA_PREFIX = /^\s*[=+\-@]/;
function csvCell(value) {
    if (value === null || value === undefined)
        return '';
    if (typeof value === 'number')
        return Number.isFinite(value) ? String(value) : '';
    const guarded = FORMULA_PREFIX.test(value) ? `'${value}` : value;
    const escaped = guarded.replace(/"/g, '""');
    return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
}
/** The text of a cell as written by `csvCell`, reading the formula guard back. */
const unwrap = (cell) => (cell ?? '').replace(/^'(?=\s*[=+\-@])/, '').trim();
/** Header plus one row per SKU, CRLF like the other exports (the download adds the BOM). */
export function buildBaseGarmentsCsv(rows) {
    const lines = [BASE_GARMENT_CSV_COLUMNS.join(',')];
    for (const row of rows) {
        lines.push([
            row.sku,
            row.code,
            row.name,
            row.size,
            row.colour,
            baseGarmentStockTypeText(row),
            row.printMethod,
            row.weightG,
            row.costUsd === null ? null : row.costUsd.toFixed(2),
            row.lowStockAlert,
            row.supplierName,
            row.hsCode,
            row.stock,
            row.imageUrl,
        ]
            .map(csvCell)
            .join(','));
    }
    return lines.join('\r\n');
}
/** RFC 4180 reader: quoted fields may hold commas, quotes and line breaks. Blank records are dropped. */
function readCsvRecords(text) {
    const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
    const records = [];
    let cells = [];
    let field = '';
    let inQuotes = false;
    let line = 1;
    let recordLine = 1;
    const endRecord = () => {
        cells.push(field);
        if (cells.some((cell) => cell.trim() !== ''))
            records.push({ line: recordLine, cells });
        cells = [];
        field = '';
    };
    for (let i = 0; i < src.length; i++) {
        const char = src[i];
        if (inQuotes) {
            if (char === '"') {
                if (src[i + 1] === '"') {
                    field += '"';
                    i++;
                }
                else {
                    inQuotes = false;
                }
            }
            else {
                if (char === '\n')
                    line++;
                field += char;
            }
            continue;
        }
        if (char === '"') {
            inQuotes = true;
        }
        else if (char === ',') {
            cells.push(field);
            field = '';
        }
        else if (char === '\n' || char === '\r') {
            if (char === '\r' && src[i + 1] === '\n')
                i++;
            endRecord();
            line++;
            recordLine = line;
        }
        else {
            field += char;
        }
    }
    if (inQuotes)
        return { records: [], unterminatedQuote: true };
    endRecord();
    return { records, unterminatedQuote: false };
}
/**
 * Header spellings read as a column: the stock type column's old name, and both
 * spelled out in words. A file with two spellings of one column has it twice.
 */
const CSV_HEADER_ALIASES = {
    who_can_use: 'stock_type',
    'who can use it': 'stock_type',
    'stock type': 'stock_type',
};
const headerKey = (text) => {
    const key = unwrap(text).toLowerCase();
    return CSV_HEADER_ALIASES[key] ?? key;
};
/** Split the file; refuse only what makes a diff impossible. */
export function parseBaseGarmentsCsv(text, filename) {
    const { records, unterminatedQuote } = readCsvRecords(text);
    if (unterminatedQuote)
        return { errors: [{ code: 'unterminatedQuote' }] };
    const [headerRecord, ...rest] = records;
    if (headerRecord === undefined)
        return { errors: [{ code: 'noRows' }] };
    const headers = headerRecord.cells;
    const keys = headers.map(headerKey);
    if (!keys.includes('sku') && !keys.includes('product_code'))
        return { errors: [{ code: 'missingKey' }] };
    const seen = new Set();
    const duplicates = [];
    keys.forEach((key, i) => {
        if (key === '')
            return;
        if (seen.has(key))
            duplicates.push({ code: 'duplicateColumn', column: unwrap(headers[i]) });
        seen.add(key);
    });
    if (duplicates.length > 0)
        return { errors: duplicates };
    const rows = [];
    const errors = [];
    for (const record of rest) {
        const cells = [...record.cells];
        if (cells.length > headers.length) {
            if (cells.slice(headers.length).some((cell) => cell.trim() !== '')) {
                errors.push({ code: 'fieldCount', line: record.line, expected: headers.length, got: cells.length });
                continue;
            }
            cells.length = headers.length;
        }
        // A spreadsheet may drop trailing blank cells: blank means "keep".
        while (cells.length < headers.length)
            cells.push('');
        rows.push({ line: record.line, cells });
    }
    if (errors.length > 0)
        return { errors };
    if (rows.length === 0)
        return { errors: [{ code: 'noRows' }] };
    return { data: { filename, headers, rows } };
}
/** A code-table name as the SQL compares it: trimmed, any case. */
export const codeNameKey = (name) => name.trim().toLowerCase();
/** The names a size or colour code table holds, as `codeNameKey`s. */
export const codeTableNameKeys = (table) => new Set(Object.keys(table).map(codeNameKey));
/**
 * The name a new size or colour code goes into its code table under: the name
 * itself (trimmed, at most 64 characters) when no entry has it yet, else
 * `<name> (<CODE>)`, the way "Add these codes" names a clash. `takenKeys` holds
 * `codeNameKey`s. The result may still be taken: the caller checks it.
 */
export function codeNameWithSuffix(name, code, takenKeys) {
    const base = name.trim().slice(0, BASE_GARMENT_SEGMENT_NAME_MAX).trim() || code;
    if (!takenKeys.has(codeNameKey(base)))
        return base;
    const suffix = ` (${code})`;
    return `${base.slice(0, BASE_GARMENT_SEGMENT_NAME_MAX - suffix.length).trim()}${suffix}`;
}
/** A clear: the dash, ASCII or the full-width one a Chinese keyboard types. */
const isClear = (text) => text === '-' || text === '－';
/** Full-width digits and punctuation a Chinese keyboard types, folded to ASCII. */
const foldNumeric = (text) => text
    .replace(/[０-９]/g, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0xfee0))
    .replace(/[．。]/g, '.')
    .replace(/[，]/g, ',')
    .replace(/,/g, '');
/**
 * The file against the export payload (the SAVED data). Blank cell = leave as
 * is; `-` clears an optional value. Refusals are per row or per file, each
 * with its line and column; any refusal blocks the whole file, so a file is
 * never partly applied.
 */
export function diffBaseGarmentsCsv(exportRows, codeTables, file, lookups = { clients: [], suppliers: [] }, options = {}) {
    const allowCost = options.allowCost !== false;
    const allowStock = options.allowStock !== false;
    const errors = [];
    // ── What the org has now ──────────────────────────────────────────────
    const bySku = new Map();
    const codes = new Map();
    const clients = new Map();
    for (const client of lookups.clients)
        clients.set(client.id, client.name);
    for (const row of exportRows) {
        bySku.set(row.sku.toUpperCase(), row);
        if (row.clientOrganizationId && row.clientName)
            clients.set(row.clientOrganizationId, row.clientName);
        let info = codes.get(row.code);
        if (!info) {
            info = {
                name: row.name,
                audience: row.audience,
                clientOrganizationId: row.clientOrganizationId,
                printMethod: row.printMethod,
                lowStockAlert: row.lowStockAlert,
                supplierId: row.supplierId,
                hsCode: row.hsCode,
                sized: row.size !== null,
                sizeSegs: new Set(),
                colourSegs: new Set(),
            };
            codes.set(row.code, info);
        }
        if (row.size !== null)
            info.sizeSegs.add(row.size);
        info.colourSegs.add(row.colour);
    }
    const knownCodes = new Set([...codes.keys(), ...(options.knownCodes ?? [])]);
    const sizeByName = new Map();
    for (const [name, code] of Object.entries(codeTables.sizes))
        sizeByName.set(name.trim().toLowerCase(), code.toUpperCase());
    const colourByName = new Map();
    for (const [name, code] of Object.entries(codeTables.colours))
        colourByName.set(name.trim().toLowerCase(), code.toUpperCase());
    const sizeCodes = new Set(Object.values(codeTables.sizes).map((code) => code.toUpperCase()));
    const colourCodes = new Set(Object.values(codeTables.colours).map((code) => code.toUpperCase()));
    const colourless = codeTables.colourlessCode.toUpperCase();
    // A segment is known when the code tables hold it, or the code already uses it.
    const sizeKnown = (seg, info) => sizeCodes.has(seg) || (info?.sizeSegs.has(seg) ?? false);
    const colourKnown = (seg, info) => colourCodes.has(seg) || seg === colourless || (info?.colourSegs.has(seg) ?? false);
    // Never offered as a new code: a code on the other axis, and the colourless code
    // (the org's and the default) as a size. The default colourless code is no new
    // colour either: the add refuses it.
    const sizeNeverNew = (seg, info) => colourKnown(seg, info) || seg === DEFAULT_COLOURLESS_CODE;
    const colourNeverNew = (seg, info) => sizeKnown(seg, info) || seg === DEFAULT_COLOURLESS_CODE;
    // ── Headers ───────────────────────────────────────────────────────────
    const columnIndex = new Map();
    const ignoredColumns = [];
    file.headers.forEach((raw, col) => {
        const key = headerKey(raw);
        if (key === '')
            return;
        const known = BASE_GARMENT_CSV_COLUMNS.includes(key);
        if (known)
            columnIndex.set(key, col);
        else
            ignoredColumns.push(unwrap(raw));
    });
    const headerText = (column) => unwrap(file.headers[columnIndex.get(column) ?? -1]) || column;
    // ── Rows ──────────────────────────────────────────────────────────────
    const parsed = [];
    const seenKeys = new Set();
    for (const row of file.rows) {
        const cell = (column) => {
            const col = columnIndex.get(column);
            return col === undefined ? '' : unwrap(row.cells[col]);
        };
        const skuText = cell('sku').toUpperCase();
        const fail = (code, column, value) => {
            errors.push({ code, line: row.line, column: headerText(column), sku: skuText || cell('product_code'), value });
        };
        // Identity: the SKU text, or product code + size + colour.
        let code = cell('product_code').toUpperCase();
        let sizeText = cell('size');
        let colourText = cell('colour');
        let size = null;
        let colour = '';
        // A size or colour code the tables do not hold yet is offered as a new code
        // (BLU-1435), unless it is a code on the other axis: then the row can't be read.
        let pendingSize = false;
        let pendingColour = false;
        let sizeName;
        let colourName;
        if (skuText !== '') {
            const parts = skuText.split('-');
            if ((parts.length !== 2 && parts.length !== 3) || parts.some((part, i) => !(i === 0 ? BASE_GARMENT_CODE_RE : BASE_GARMENT_SIZE_COLOUR_CODE_RE).test(part))) {
                fail('unreadableSku', 'sku', skuText);
                continue;
            }
            const skuCode = parts[0];
            const skuSize = parts.length === 3 ? parts[1] : null;
            const skuColour = parts[parts.length - 1];
            if (!bySku.has(skuText)) {
                const info = codes.get(skuCode);
                if (skuSize !== null && !sizeKnown(skuSize, info)) {
                    if (sizeNeverNew(skuSize, info)) {
                        fail('unreadableSku', 'sku', skuText);
                        continue;
                    }
                    pendingSize = true;
                }
                if (!colourKnown(skuColour, info)) {
                    if (colourNeverNew(skuColour, info)) {
                        fail('unreadableSku', 'sku', skuText);
                        continue;
                    }
                    pendingColour = true;
                }
            }
            if (code !== '' && code !== skuCode) {
                fail('skuMismatch', 'product_code', cell('product_code'));
                continue;
            }
            // A cell that differs from its segment must be that segment's table name. For a
            // new segment, a cell that is no code and no table name is the new code's name.
            if (sizeText !== '' && !sameText(sizeText, skuSize ?? '')) {
                const resolved = sizeByName.get(sizeText.toLowerCase());
                if (resolved !== skuSize) {
                    if (!(pendingSize && resolved === undefined && !BASE_GARMENT_SIZE_COLOUR_CODE_RE.test(sizeText))) {
                        fail('skuMismatch', 'size', sizeText);
                        continue;
                    }
                    sizeName = sizeText;
                }
            }
            if (colourText !== '' && !sameText(colourText, skuColour)) {
                const resolved = colourByName.get(colourText.toLowerCase());
                if (resolved !== skuColour) {
                    if (!(pendingColour && resolved === undefined && !BASE_GARMENT_SIZE_COLOUR_CODE_RE.test(colourText))) {
                        fail('skuMismatch', 'colour', colourText);
                        continue;
                    }
                    colourName = colourText;
                }
            }
            code = skuCode;
            size = skuSize;
            colour = skuColour;
        }
        else {
            if (code === '') {
                fail('missingKey', 'sku', '');
                continue;
            }
            if (!BASE_GARMENT_CODE_RE.test(code)) {
                fail('badCode', 'product_code', code);
                continue;
            }
            const info = codes.get(code);
            // A known code first, then a table name; only text written as a code (capitals,
            // 1 to 6 letters or digits) becomes a new code. A plain name stays an error.
            if (sizeText !== '') {
                const upper = sizeText.toUpperCase();
                let resolved = sizeKnown(upper, info) ? upper : sizeByName.get(sizeText.toLowerCase());
                if (!resolved && BASE_GARMENT_SIZE_COLOUR_CODE_RE.test(sizeText)) {
                    if (sizeNeverNew(sizeText, info)) {
                        fail('segmentWrongAxis', 'size', sizeText);
                        continue;
                    }
                    resolved = sizeText;
                    pendingSize = true;
                }
                if (!resolved) {
                    fail('unknownSize', 'size', sizeText);
                    continue;
                }
                size = resolved;
            }
            if (colourText === '') {
                colour = colourless;
            }
            else {
                const upper = colourText.toUpperCase();
                let resolved = colourKnown(upper, info) ? upper : colourByName.get(colourText.toLowerCase());
                if (!resolved && BASE_GARMENT_SIZE_COLOUR_CODE_RE.test(colourText)) {
                    if (sizeKnown(colourText, info)) {
                        fail('segmentWrongAxis', 'colour', colourText);
                        continue;
                    }
                    if (!colourNeverNew(colourText, info)) {
                        resolved = colourText;
                        pendingColour = true;
                    }
                }
                if (!resolved) {
                    fail('unknownColour', 'colour', colourText);
                    continue;
                }
                colour = resolved;
            }
        }
        const key = `${code}|${size ?? ''}|${colour}`;
        if (seenKeys.has(key)) {
            fail('duplicateRow', skuText !== '' ? 'sku' : 'product_code', composeBlankSku(code, size, colour));
            continue;
        }
        seenKeys.add(key);
        const entry = {
            line: row.line,
            code,
            size,
            colour,
            source: { sku: skuText, size: sizeText, colour: colourText },
        };
        if (pendingSize)
            entry.pendingSize = true;
        if (pendingColour)
            entry.pendingColour = true;
        if (sizeName !== undefined)
            entry.sizeName = sizeName;
        if (colourName !== undefined)
            entry.colourName = colourName;
        let rowOk = true;
        const name = cell('name');
        if (name !== '')
            entry.name = name.slice(0, 120);
        const who = cell('stock_type');
        if (who !== '') {
            const stockType = readBaseGarmentStockType(who);
            if (stockType === null) {
                fail('unknownWhoCanUse', 'stock_type', who);
                rowOk = false;
            }
            else if (stockType.audience !== 'client') {
                entry.who = { audience: stockType.audience, clientOrganizationId: null };
            }
            else {
                const matches = [...clients.entries()].filter(([, n]) => sameText(n, stockType.clientName));
                if (matches.length === 1)
                    entry.who = { audience: 'client', clientOrganizationId: matches[0][0] };
                else {
                    fail(matches.length === 0 ? 'unknownClient' : 'ambiguousClient', 'stock_type', who);
                    rowOk = false;
                }
            }
        }
        const method = cell('print_method');
        if (method !== '') {
            const upper = method.toUpperCase();
            if (upper === 'DTG' || upper === 'DTF')
                entry.printMethod = upper;
            else {
                fail('badPrintMethod', 'print_method', method);
                rowOk = false;
            }
        }
        const numberCell = (column, accept) => {
            const text = foldNumeric(cell(column));
            if (text === '')
                return undefined;
            if (isClear(text))
                return null;
            const value = accept(text);
            if (value === undefined) {
                fail('badNumber', column, cell(column));
                rowOk = false;
                return undefined;
            }
            return value;
        };
        const wholeNumber = (text) => /^\d{1,7}$/.test(text) && Number(text) <= BASE_GARMENT_STOCK_MAX ? Number(text) : undefined;
        const weight = numberCell('weight_g', (text) => {
            const value = /^\d+(\.\d+)?$/.test(text) ? Number(text) : NaN;
            return value > 0 && value <= BASE_GARMENT_WEIGHT_MAX_GRAMS ? value : undefined;
        });
        if (weight !== undefined)
            entry.weightG = weight;
        if (allowCost) {
            const cost = numberCell('cost_usd', (text) => (CLIENT_PRICE_REGEX.test(text) ? Number(text) : undefined));
            if (cost !== undefined)
                entry.costUsd = cost;
        }
        const alert = numberCell('low_stock_alert', wholeNumber);
        if (alert !== undefined)
            entry.lowStockAlert = alert;
        if (allowStock) {
            const stock = numberCell('stock', wholeNumber);
            if (stock !== undefined && stock !== null)
                entry.stock = stock;
            else if (stock === null) {
                fail('badNumber', 'stock', cell('stock'));
                rowOk = false;
            }
        }
        // The supplier is cost data: without the cost permission the column is ignored, like cost_usd.
        const supplier = allowCost ? cell('supplier') : '';
        if (supplier !== '') {
            if (isClear(supplier))
                entry.supplierId = null;
            else {
                const matches = lookups.suppliers.filter((s) => sameText(s.name, supplier));
                if (matches.length === 1)
                    entry.supplierId = matches[0].id;
                else {
                    fail(matches.length === 0 ? 'unknownSupplier' : 'ambiguousSupplier', 'supplier', supplier);
                    rowOk = false;
                }
            }
        }
        const hs = cell('hs_code');
        if (hs !== '')
            entry.hsCode = isClear(hs) ? null : hs.slice(0, 20);
        const image = cell('image_url');
        if (image !== '') {
            if (isClear(image))
                entry.imageUrl = null;
            else if (image.length <= 2048 && BASE_GARMENT_IMAGE_URL_RE.test(image))
                entry.imageUrl = image;
            else {
                fail('badImage', 'image_url', image);
                rowOk = false;
            }
        }
        if (rowOk)
            parsed.push(entry);
    }
    // ── New size and colour codes ─────────────────────────────────────────
    // One entry per code and kind, at its first line. A code new on BOTH axes in
    // one file can't be read either way, so its rows are refused.
    const pending = {
        sizes: new Map(),
        colours: new Map(),
    };
    const notePending = (kind, code, line, name) => {
        const at = pending[kind].get(code);
        if (!at)
            pending[kind].set(code, name === undefined ? { line } : { line, name });
        else if (at.name === undefined && name !== undefined)
            at.name = name;
    };
    for (const row of parsed) {
        if (row.pendingSize && row.size !== null)
            notePending('sizes', row.size, row.line, row.sizeName);
        if (row.pendingColour)
            notePending('colours', row.colour, row.line, row.colourName);
    }
    const bothAxes = new Set([...pending.sizes.keys()].filter((segment) => pending.colours.has(segment)));
    if (bothAxes.size > 0) {
        const kept = parsed.filter((row) => {
            const badSize = row.pendingSize === true && row.size !== null && bothAxes.has(row.size);
            const badColour = row.pendingColour === true && bothAxes.has(row.colour);
            if (!badSize && !badColour)
                return true;
            const column = row.source.sku !== '' ? 'sku' : badSize ? 'size' : 'colour';
            errors.push({
                code: row.source.sku !== '' ? 'unreadableSku' : 'segmentWrongAxis',
                line: row.line,
                column: headerText(column),
                sku: row.source.sku || row.code,
                value: row.source.sku || (badSize ? row.source.size : row.source.colour),
            });
            return false;
        });
        parsed.splice(0, parsed.length, ...kept);
        for (const segment of bothAxes) {
            pending.sizes.delete(segment);
            pending.colours.delete(segment);
        }
    }
    const newSegmentCodes = [];
    for (const kind of ['sizes', 'colours']) {
        const table = codeTables[kind];
        const taken = codeTableNameKeys(table);
        for (const [segment, at] of pending[kind]) {
            const name = codeNameWithSuffix(at.name ?? options.segmentNames?.[kind]?.[segment] ?? segment, segment, taken);
            taken.add(codeNameKey(name));
            newSegmentCodes.push({ kind, code: segment, name, line: at.line });
        }
        const over = Object.keys(table).length + pending[kind].size - BASE_GARMENT_CODE_TABLE_MAX;
        if (pending[kind].size > 0 && over > 0) {
            errors.push({
                code: 'codeTableFull',
                line: null,
                column: kind === 'sizes' ? 'size' : 'colour',
                sku: '',
                value: String(over),
            });
        }
    }
    // ── Rows that disagree within a code, size or colour ──────────────────
    const disagree = (level, column, groupOf, valueOf) => {
        const groups = new Map();
        for (const row of parsed) {
            const value = valueOf(row);
            if (value === undefined)
                continue;
            const group = groups.get(groupOf(row)) ?? new Map();
            if (!group.has(value))
                group.set(value, row);
            groups.set(groupOf(row), group);
        }
        for (const group of groups.values()) {
            if (group.size < 2)
                continue;
            const [, ...others] = [...group.values()];
            for (const row of others) {
                errors.push({
                    code: level,
                    line: row.line,
                    column: headerText(column),
                    sku: composeBlankSku(row.code, row.size, row.colour),
                    value: valueOf(row) ?? '',
                });
            }
        }
    };
    const whoText = (row) => row.who ? `${row.who.audience}:${row.who.clientOrganizationId ?? ''}` : undefined;
    const codeLevel = [
        ['name', (row) => row.name],
        ['stock_type', whoText],
        ['print_method', (row) => row.printMethod],
        ['low_stock_alert', (row) => (row.lowStockAlert === undefined ? undefined : String(row.lowStockAlert))],
        ['supplier', (row) => (row.supplierId === undefined ? undefined : String(row.supplierId))],
        ['hs_code', (row) => (row.hsCode === undefined ? undefined : String(row.hsCode))],
    ];
    for (const [column, valueOf] of codeLevel)
        disagree('codeDisagrees', column, (row) => row.code, valueOf);
    disagree('sizeDisagrees', 'weight_g', (row) => `${row.code}|${row.size ?? ''}`, (row) => row.weightG === undefined ? undefined : String(row.weightG));
    disagree('colourDisagrees', 'image_url', (row) => `${row.code}|${row.colour}`, (row) => row.imageUrl === undefined ? undefined : String(row.imageUrl));
    // ── One shape per code ────────────────────────────────────────────────
    const shapeByCode = new Map();
    for (const row of parsed) {
        const sized = row.size !== null;
        const known = shapeByCode.get(row.code) ?? codes.get(row.code)?.sized;
        if (known !== undefined && known !== sized) {
            errors.push({
                code: 'shapeMix',
                line: row.line,
                column: headerText(sized ? 'size' : 'colour'),
                sku: composeBlankSku(row.code, row.size, row.colour),
                value: row.size ?? '',
            });
            continue;
        }
        shapeByCode.set(row.code, sized);
    }
    // ── New codes ─────────────────────────────────────────────────────────
    const newCodes = [];
    const newCodeSet = new Set();
    for (const row of parsed) {
        if (knownCodes.has(row.code) || newCodeSet.has(row.code))
            continue;
        newCodeSet.add(row.code);
        const rowsOfCode = parsed.filter((r) => r.code === row.code);
        const name = rowsOfCode.find((r) => r.name !== undefined)?.name;
        const who = rowsOfCode.find((r) => r.who !== undefined)?.who;
        if (!NEW_PRODUCT_CODE_RE.test(row.code)) {
            errors.push({ code: 'badCode', line: row.line, column: headerText('product_code'), sku: row.code, value: row.code });
            continue;
        }
        if (name === undefined) {
            errors.push({ code: 'missingName', line: row.line, column: headerText('name'), sku: row.code, value: row.code });
            continue;
        }
        newCodes.push({
            code: row.code,
            name,
            audience: who?.audience ?? null,
            clientOrganizationId: who?.clientOrganizationId ?? null,
            line: row.line,
        });
    }
    // ── Against the saved data ────────────────────────────────────────────
    const centsEqual = (a, b) => a === null || b === null ? a === b : Math.round(a * 100) === Math.round(b * 100);
    const rows = [];
    const rowLines = [];
    let updatedRows = 0;
    let newSkuRows = 0;
    let unchangedRows = 0;
    let stockChanges = 0;
    for (const row of parsed) {
        const isNewCode = newCodeSet.has(row.code);
        const existing = bySku.get(composeBlankSku(row.code, row.size, row.colour));
        const info = codes.get(row.code);
        const out = { code: row.code, size: row.size, colour: row.colour };
        let changed = existing === undefined;
        if (!isNewCode && info) {
            if (row.name !== undefined && row.name !== info.name) {
                out.name = row.name;
                changed = true;
            }
            if (row.who && (row.who.audience !== info.audience || row.who.clientOrganizationId !== info.clientOrganizationId)) {
                out.audience = row.who.audience;
                out.clientOrganizationId = row.who.clientOrganizationId;
                changed = true;
            }
            if (row.printMethod !== undefined && row.printMethod !== info.printMethod) {
                out.printMethod = row.printMethod;
                changed = true;
            }
            if (row.lowStockAlert !== undefined && row.lowStockAlert !== info.lowStockAlert) {
                out.lowStockAlert = row.lowStockAlert;
                changed = true;
            }
            if (row.supplierId !== undefined && row.supplierId !== info.supplierId) {
                out.supplierId = row.supplierId;
                changed = true;
            }
            if (row.hsCode !== undefined && row.hsCode !== info.hsCode) {
                out.hsCode = row.hsCode;
                changed = true;
            }
        }
        else {
            if (row.printMethod !== undefined)
                out.printMethod = row.printMethod;
            if (row.lowStockAlert !== undefined)
                out.lowStockAlert = row.lowStockAlert;
            if (row.supplierId !== undefined)
                out.supplierId = row.supplierId;
            if (row.hsCode !== undefined)
                out.hsCode = row.hsCode;
        }
        if (row.weightG !== undefined && (existing === undefined || row.weightG !== existing.weightG)) {
            out.weightG = row.weightG;
            changed = true;
        }
        if (row.costUsd !== undefined && (existing === undefined || !centsEqual(row.costUsd, existing.costUsd))) {
            out.costUsd = row.costUsd;
            changed = true;
        }
        if (row.imageUrl !== undefined && (existing === undefined || row.imageUrl !== existing.imageUrl)) {
            out.imageUrl = row.imageUrl;
            changed = true;
        }
        if (row.stock !== undefined && (existing === undefined || row.stock !== existing.stock)) {
            out.stock = row.stock;
            stockChanges += 1;
            changed = true;
        }
        if (existing === undefined)
            newSkuRows += 1;
        else if (changed)
            updatedRows += 1;
        else
            unchangedRows += 1;
        if (changed) {
            rows.push(out);
            rowLines.push(row.line);
        }
    }
    if (rows.length > BASE_GARMENT_MAX_IMPORT_ROWS) {
        errors.push({ code: 'tooMany', line: null, column: '', sku: '', value: String(rows.length) });
    }
    else if (errors.length === 0 && rows.length === 0) {
        errors.push({ code: 'noChanges', line: null, column: '', sku: '', value: '' });
    }
    return {
        rows,
        rowLines,
        newCodes,
        newSegmentCodes,
        errors,
        ignoredColumns,
        counts: { fileRows: file.rows.length, updatedRows, newSkuRows, unchangedRows, stockChanges },
    };
}
/**
 * Apply is allowed with no refusal, at least one change, and a who-can-use pick for
 * every new code. New size and colour codes do not block it: the caller checks
 * their names and adds them to the code tables before the save.
 */
export function canApplyBaseGarmentsCsv(diff, picks = {}) {
    if (diff.errors.length > 0 || diff.rows.length === 0)
        return false;
    return diff.newCodes.every((entry) => {
        const audience = picks[entry.code]?.audience ?? entry.audience;
        const client = picks[entry.code]?.clientOrganizationId ?? entry.clientOrganizationId;
        return audience !== null && (audience === 'client') === (client !== null && client !== undefined);
    });
}
/** The `newCodeAudiences` of the apply call: the file's own stock_type, overridden by the picks. */
export function buildNewCodeAudiences(diff, picks = {}) {
    const out = [];
    for (const entry of diff.newCodes) {
        const audience = picks[entry.code]?.audience ?? entry.audience;
        if (audience === null)
            continue;
        const client = picks[entry.code]?.clientOrganizationId ?? entry.clientOrganizationId;
        out.push({
            code: entry.code,
            name: entry.name,
            audience,
            ...(audience === 'client' && client ? { clientOrganizationId: client } : {}),
        });
    }
    return out;
}
